/**
 * ============================================================================
 * GOOGLE DRIVE — REST CLIENT (Stage 3b)
 * ============================================================================
 *
 * Thin wrappers over the Google Drive API v3 REST endpoints, using native
 * fetch() — no SDK, no new npm dependency. This file has NO app-specific
 * logic (no folder name, no backup format, no "LIC HFL" anything) — it
 * is pure HTTP plumbing that driveFolder.ts and driveBackup.ts build on.
 * Keeping this separation means the one place that could get Drive's
 * request/response shapes wrong is small and independently testable.
 *
 * SECURITY: never logs the access token or response bodies (which may
 * contain the backup's financial data). Errors surface an HTTP status
 * and a short reason string only.
 */

const DRIVE_FILES_URL = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files';

export class DriveApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'DriveApiError';
  }
}

export interface DriveFile {
  id: string;
  name: string;
  mimeType?: string;
  createdTime?: string;
  appProperties?: Record<string, string>;
  parents?: string[];
}

interface DriveFilesListResponse {
  files?: DriveFile[];
}

function authHeaders(accessToken: string): HeadersInit {
  return { Authorization: `Bearer ${accessToken}` };
}

async function assertOk(response: Response, action: string): Promise<void> {
  if (!response.ok) {
    throw new DriveApiError(response.status, `Google Drive request failed (${action}): HTTP ${response.status}`);
  }
}

/**
 * Wraps GET /files with an arbitrary query string (Drive's `q` search
 * syntax — see driveFolder.ts for the actual query used to find the
 * backup folder). `fields` follows Drive's partial-response syntax,
 * e.g. 'files(id,name,createdTime,appProperties)'.
 */
export async function driveFilesList(
  accessToken: string,
  query: string,
  fields: string,
  options?: { orderBy?: string; pageSize?: number }
): Promise<DriveFile[]> {
  const url = new URL(DRIVE_FILES_URL);
  url.searchParams.set('q', query);
  url.searchParams.set('fields', `nextPageToken, ${fields}`);
  url.searchParams.set('spaces', 'drive');
  if (options?.orderBy) url.searchParams.set('orderBy', options.orderBy);
  if (options?.pageSize) url.searchParams.set('pageSize', String(options.pageSize));
  const response = await fetch(url.toString(), { headers: authHeaders(accessToken) });
  await assertOk(response, 'list files');
  const data = (await response.json()) as DriveFilesListResponse;
  return data.files ?? [];
}

/**
 * Creates a metadata-only file — used for folders, which have no
 * content to upload (see driveFolder.ts).
 */
export async function driveCreateMetadataOnly(
  accessToken: string,
  metadata: { name: string; mimeType: string; parents?: string[]; appProperties?: Record<string, string> }
): Promise<DriveFile> {
  const response = await fetch(DRIVE_FILES_URL, {
    method: 'POST',
    headers: { ...authHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify(metadata),
  });
  await assertOk(response, 'create folder');
  return (await response.json()) as DriveFile;
}

/**
 * Multipart upload: metadata (name/parents/appProperties) + JSON content
 * in a single request. Appropriate for this app's backup files, which
 * are always small (a personal loan's event history, not bulk data) —
 * well within the 5MB multipart-upload limit, so no resumable-upload
 * complexity is needed.
 */
export async function driveUploadJson(
  accessToken: string,
  metadata: { name: string; mimeType?: string; parents: string[]; appProperties?: Record<string, string> },
  jsonContent: string
): Promise<DriveFile> {
  const boundary = `-------lic-hfl-backup-${Date.now()}`;
  const body =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    JSON.stringify(metadata) +
    `\r\n--${boundary}\r\n` +
    'Content-Type: application/json\r\n\r\n' +
    jsonContent +
    `\r\n--${boundary}--`;

  const response = await fetch(`${DRIVE_UPLOAD_URL}?uploadType=multipart`, {
    method: 'POST',
    headers: { ...authHeaders(accessToken), 'Content-Type': `multipart/related; boundary=${boundary}` },
    body,
  });
  await assertOk(response, 'upload backup');
  const file = (await response.json()) as DriveFile;
  // Verify Google actually accepted and identified the file before the
  // caller reports success — an ok HTTP status with no file id would be
  // an unexpected/malformed response, not a genuine success.
  if (!file.id) {
    throw new DriveApiError(response.status, 'Google Drive did not confirm the upload (no file id returned).');
  }
  return file;
}

/** Downloads a file's raw content as text — used to fetch a backup JSON file for restore. */
export async function driveDownloadFile(accessToken: string, fileId: string): Promise<string> {
  const url = new URL(`${DRIVE_FILES_URL}/${encodeURIComponent(fileId)}`);
  url.searchParams.set('alt', 'media');
  const response = await fetch(url.toString(), { headers: authHeaders(accessToken) });
  await assertOk(response, 'download backup');
  return response.text();
}

/**
 * Permanently deletes ONE file by id. Only ever called by retention
 * (driveBackup.ts) on files that have already passed every backup-file
 * identification check. A 404 means the file is already gone (e.g. a
 * concurrent cleanup removed it first), which is the outcome retention
 * wanted, so it is not treated as a failure.
 */
export async function driveDeleteFile(accessToken: string, fileId: string): Promise<void> {
  const response = await fetch(`${DRIVE_FILES_URL}/${encodeURIComponent(fileId)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (response.status === 404) return;
  await assertOk(response, 'delete old backup');
}
